/**
 * `diagram/ganttLayout.ts` — pure date-resolution + pixel layout for the modern gantt canvas
 * (redesign R1). Resolves each task to an absolute `[start,end]` (topological, cycle-safe) and lays
 * out a split task-panel + timeline: rows, a two-tier date axis (month band over day/week ticks),
 * weekend shading, a today line, rounded bars with a percent-complete fill, and finish-to-start
 * dependency elbows. Zoom (day/week/month) sets px-per-day + axis granularity. No React/DOM.
 */

import { ganttTasks, type GanttSpec, type GanttStatus, type GanttTask } from './gantt';

// ── spec mutators (drag edits) ───────────────────────────────────────────────

/** Fields the metadata inspector can set on a task. */
export type GanttTaskPatch = Partial<
  Pick<
    GanttTask,
    | 'title'
    | 'status'
    | 'owner'
    | 'milestone'
    | 'start'
    | 'after'
    | 'duration'
    | 'end'
    | 'description'
    | 'fields'
  >
>;

/**
 * Apply a field patch to a task, enforcing the mermaid gantt invariants so the serialized fence is
 * always valid: a literal `start` and `after` are exclusive anchors; `duration` and `end` are
 * exclusive lengths; a milestone is a zero-length point (no duration/end). The inspector edits one
 * concern at a time, and this keeps the other, now-incompatible field from lingering.
 */
export function updateTask(spec: GanttSpec, id: string, patch: GanttTaskPatch): GanttSpec {
  return mapTask(spec, id, (t) => {
    const next: GanttTask = { ...t, ...patch };
    if (patch.start) next.after = [];
    if (patch.after && patch.after.length > 0) next.start = null;
    if (next.milestone) {
      next.duration = null;
      next.end = null;
    } else {
      if (patch.duration) next.end = null;
      if (patch.end) next.duration = null;
    }
    return next;
  });
}

function mapTask(spec: GanttSpec, id: string, fn: (t: GanttTask) => GanttTask): GanttSpec {
  return {
    ...spec,
    sections: spec.sections.map((s) => ({ ...s, tasks: s.tasks.map((t) => (t.id === id ? fn(t) : t)) })),
  };
}

/** Shift a **date-anchored** task's start by `deltaDays` (an `after`-anchored task is date-derived). */
export function rescheduleTask(spec: GanttSpec, id: string, deltaDays: number): GanttSpec {
  if (deltaDays === 0) return spec;
  return mapTask(spec, id, (t) => {
    if (!t.start) return t;
    const d = parseGanttDate(t.start);
    return d ? { ...t, start: fmtDate(addDays(d, deltaDays)) } : t;
  });
}

/** Set a task's duration to `durationDays` whole days (min 1). Milestones unaffected; clears `end`. */
export function resizeTask(spec: GanttSpec, id: string, durationDays: number): GanttSpec {
  const days = Math.max(1, Math.round(durationDays));
  return mapTask(spec, id, (t) => (t.milestone ? t : { ...t, duration: `${days}d`, end: null }));
}

/**
 * Left-edge resize: shift a task's **start** by `deltaDays` while holding its finish date, so the
 * duration absorbs the change (min 1 day). Only meaningful for a date-anchored, non-milestone task —
 * an `after`-anchored start is derived, so there is nothing to drag.
 */
export function resizeTaskStart(spec: GanttSpec, id: string, deltaDays: number): GanttSpec {
  if (deltaDays === 0) return spec;
  const resolved = resolveTasks(spec);
  return mapTask(spec, id, (t) => {
    if (t.milestone || !t.start) return t;
    const r = resolved.get(id);
    const anchor = parseGanttDate(t.start);
    if (!r || !anchor) return t;
    const span = Math.max(1, dayDiff(r.start, r.end));
    const days = Math.max(1, span - Math.round(deltaDays));
    // Re-derive the shift from the clamped duration so start/end stay consistent at the 1-day floor.
    return { ...t, start: fmtDate(addDays(anchor, span - days)), duration: `${days}d`, end: null };
  });
}

/**
 * Label sanitiser for user-typed names. The fence format reserves `:` (label/tail separator) and
 * ` · ` (title/owner separator), and a task line is one line — so those are the only things a rename
 * may not carry. Everything else round-trips.
 */
function sanitizeLabel(text: string): string {
  return text
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s·\s/g, ' - ')
    .replace(/:/g, '-')
    .trim();
}

/** Rename a task (its visible title; id and owner are untouched). An all-blank name is ignored. */
export function renameTask(spec: GanttSpec, id: string, title: string): GanttSpec {
  const clean = sanitizeLabel(title);
  if (clean === '') return spec;
  return mapTask(spec, id, (t) => (t.title === clean ? t : { ...t, title: clean }));
}

/** Rename the section at `index`. An all-blank name is ignored (a section header needs a label). */
export function renameSection(spec: GanttSpec, index: number, name: string): GanttSpec {
  const clean = sanitizeLabel(name);
  if (clean === '' || !spec.sections[index]) return spec;
  return { ...spec, sections: spec.sections.map((s, i) => (i === index ? { ...s, name: clean } : s)) };
}

/**
 * Drop tasks by id, keeping the rest of the chart dated: any survivor that depended on a dropped
 * task loses that `after` link, and one left with **no** anchor at all is pinned to the start date it
 * already had — otherwise deleting one row would silently un-date everything downstream of it.
 */
function dropTasks(spec: GanttSpec, ids: ReadonlySet<string>): GanttSpec {
  const resolved = resolveTasks(spec); // resolved *before* the drop — the dates to pin to
  return {
    ...spec,
    sections: spec.sections.map((s) => ({
      ...s,
      tasks: s.tasks
        .filter((t) => !ids.has(t.id))
        .map((t) => {
          if (!t.after.some((a) => ids.has(a))) return t;
          const after = t.after.filter((a) => !ids.has(a));
          if (after.length > 0 || t.start) return { ...t, after };
          const r = resolved.get(t.id);
          return { ...t, after, start: r ? fmtDate(r.start) : null };
        }),
    })),
  };
}

/** Delete one task row. Dependents are re-anchored so the rest of the chart keeps its dates. */
export function deleteTask(spec: GanttSpec, id: string): GanttSpec {
  return dropTasks(spec, new Set([id]));
}

/** Delete the section at `index` along with every task in it. */
export function deleteSection(spec: GanttSpec, index: number): GanttSpec {
  const section = spec.sections[index];
  if (!section) return spec;
  const dropped = dropTasks(spec, new Set(section.tasks.map((t) => t.id)));
  return { ...dropped, sections: dropped.sections.filter((_, i) => i !== index) };
}

// ── date helpers ─────────────────────────────────────────────────────────────

export interface ResolvedTask {
  id: string;
  start: Date;
  end: Date;
  milestone: boolean;
  task: GanttTask;
}

const MS_PER_DAY = 86_400_000;

export function parseGanttDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + Math.round(days));
  return r;
}

const dayDiff = (a: Date, b: Date): number => Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
const startOfWeek = (d: Date): Date => addDays(d, -((d.getDay() + 6) % 7)); // Monday
const monthStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const nextMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 1);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Duration string (`10d`/`2w`/`8h`/`1y`) → days. Unknown → 1. Sub-day floors to ~0.5. */
export function durationToDays(d: string | null): number {
  if (!d) return 0;
  const m = /^(\d+(?:\.\d+)?)\s*([dhwy])$/i.exec(d.trim());
  if (!m) return 1;
  const n = Number(m[1]);
  switch (m[2].toLowerCase()) {
    case 'w':
      return n * 7;
    case 'h':
      return Math.max(0.5, n / 24);
    case 'y':
      return n * 365;
    default:
      return n;
  }
}

/** Resolve every task to absolute `[start, end]` dates (deps first; cycles yield no date, skipped). */
export function resolveTasks(spec: GanttSpec): Map<string, ResolvedTask> {
  const tasks = ganttTasks(spec);
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const resolved = new Map<string, ResolvedTask>();
  const visiting = new Set<string>();

  const resolve = (t: GanttTask): ResolvedTask | null => {
    const existing = resolved.get(t.id);
    if (existing) return existing;
    if (visiting.has(t.id)) return null;
    visiting.add(t.id);
    let start: Date | null = null;
    if (t.after.length > 0) {
      let latest: Date | null = null;
      for (const dep of t.after) {
        const d = byId.get(dep);
        if (!d) continue;
        const rd = resolve(d);
        if (rd && (latest === null || rd.end > latest)) latest = rd.end;
      }
      start = latest;
    } else if (t.start) {
      start = parseGanttDate(t.start);
    }
    visiting.delete(t.id);
    if (start === null) return null;
    let end: Date;
    if (t.milestone) end = start;
    else if (t.duration) end = addDays(start, durationToDays(t.duration));
    else if (t.end) end = parseGanttDate(t.end) ?? addDays(start, 1);
    else end = addDays(start, 1);
    const r: ResolvedTask = { id: t.id, start, end, milestone: t.milestone, task: t };
    resolved.set(t.id, r);
    return r;
  };
  for (const t of tasks) resolve(t);
  return resolved;
}

// ── view model ───────────────────────────────────────────────────────────────

export type GanttZoom = 'day' | 'week' | 'month';
const PX_PER_DAY: Record<GanttZoom, number> = { day: 26, week: 11, month: 4.6 };

/** Percent-complete inferred from status (no % field in the spec this pass). */
function percentFor(status: GanttStatus, milestone: boolean): number {
  if (milestone) return 0;
  if (status === 'done') return 1;
  if (status === 'active') return 0.45;
  return 0;
}

export interface GanttBar {
  id: string;
  title: string;
  owner: string | null;
  status: GanttStatus;
  milestone: boolean;
  percent: number;
  x: number;
  width: number;
  y: number;
  height: number;
  startDate: string;
  endDate: string;
}

export interface GanttRow {
  kind: 'section' | 'task';
  y: number;
  height: number;
  /** section */
  name?: string;
  /** section — its index in `spec.sections` (the handle for rename/delete/collapse). */
  sectionIndex?: number;
  /** section — collapsed (its task rows are hidden). */
  collapsed?: boolean;
  /** section — how many tasks it holds (shown as a count when collapsed). */
  taskCount?: number;
  /** task */
  id?: string;
  title?: string;
  owner?: string | null;
  status?: GanttStatus;
  milestone?: boolean;
  bar?: GanttBar; // absent for an undated task
}

export interface AxisBand {
  label: string;
  x: number;
  width: number;
}
export interface AxisTick {
  x: number;
  label: string;
  strong: boolean;
}
export interface DepLink {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface GanttView {
  zoom: GanttZoom;
  pxPerDay: number;
  labelWidth: number;
  /** Drawn timeline width — the natural content span, stretched to `minWidth` when there's room. */
  timelineWidth: number;
  /** Natural (data-driven) content span, ignoring any fill — used to pick a zoom in Fit. */
  contentWidth: number;
  axisHeight: number;
  bodyHeight: number;
  /** Natural content height (axis + all rows). */
  totalHeight: number;
  /** Drawn canvas height — `totalHeight` stretched to `minHeight` so the grid fills the viewport. */
  canvasHeight: number;
  rangeStart: Date | null;
  rows: GanttRow[];
  upper: AxisBand[];
  ticks: AxisTick[];
  weekends: AxisBand[];
  todayX: number | null;
  deps: DepLink[];
  hasBars: boolean;
}

export interface GanttViewOptions {
  zoom?: GanttZoom;
  today?: Date;
  labelWidth?: number;
  rowHeight?: number;
  /** Available timeline width (px); the calendar grid extends to fill it instead of leaving a void. */
  minWidth?: number;
  /** Available timeline height (px); the grid/weekend shading extends down to fill it. */
  minHeight?: number;
  /** Per-task row-height overrides (px), keyed by task id — the drag-to-resize-a-row state. */
  rowHeights?: Readonly<Record<string, number>>;
  /** Section indices that are collapsed: their task rows are omitted (the header row still shows). */
  collapsed?: ReadonlySet<number>;
}

/** Row-height bounds for the drag-to-resize grip (a row must stay clickable, and stay a row). */
export const GANTT_ROW_MIN = 22;
export const GANTT_ROW_MAX = 120;
export const GANTT_ROW_DEFAULT = 32;
/** Panel (task-list) width bounds for the drag-to-resize divider. */
export const GANTT_PANEL_MIN = 120;
export const GANTT_PANEL_MAX = 560;
export const GANTT_PANEL_DEFAULT = 220;

export const clampRowHeight = (px: number): number =>
  Math.min(GANTT_ROW_MAX, Math.max(GANTT_ROW_MIN, Math.round(px)));
export const clampPanelWidth = (px: number): number =>
  Math.min(GANTT_PANEL_MAX, Math.max(GANTT_PANEL_MIN, Math.round(px)));

/** Compute the full split-panel + timeline view for a spec at a zoom level. */
export function computeGanttView(spec: GanttSpec, opts: GanttViewOptions = {}): GanttView {
  const zoom = opts.zoom ?? 'week';
  const pxPerDay = PX_PER_DAY[zoom];
  const labelWidth = clampPanelWidth(opts.labelWidth ?? GANTT_PANEL_DEFAULT);
  const rowHeight = clampRowHeight(opts.rowHeight ?? GANTT_ROW_DEFAULT);
  const sectionH = 26;
  const axisHeight = 44;
  /** Bar thickness follows its row: a taller row gets a chunkier bar, never a floating hairline. */
  const barHeightFor = (h: number): number => Math.max(8, Math.min(28, h - 16));

  const resolved = resolveTasks(spec);
  const dated = [...resolved.values()];

  // Range (snapped to whole weeks, padded). Empty → no timeline.
  let rangeStart: Date | null = null;
  let rangeEnd: Date | null = null;
  if (dated.length > 0) {
    let min = dated[0].start;
    let max = dated[0].end;
    for (const r of dated) {
      if (r.start < min) min = r.start;
      if (r.end > max) max = r.end;
    }
    rangeStart = startOfWeek(addDays(min, -2));
    rangeEnd = addDays(startOfWeek(addDays(max, 7)), 6); // through the end of max's week + pad
  }
  let totalDays = rangeStart && rangeEnd ? Math.max(1, dayDiff(rangeStart, rangeEnd)) : 1;
  const contentWidth = rangeStart ? totalDays * pxPerDay : labelWidth;
  // Extend the date range so the timeline fills at least the available width — the calendar grid
  // runs to the edge instead of leaving a blank void (matches TeamGantt/monday on a wide viewport).
  if (rangeStart && rangeEnd && opts.minWidth && contentWidth < opts.minWidth) {
    rangeEnd = addDays(rangeEnd, Math.ceil((opts.minWidth - contentWidth) / pxPerDay));
    totalDays = Math.max(1, dayDiff(rangeStart, rangeEnd));
  }
  const timelineWidth = rangeStart ? totalDays * pxPerDay : labelWidth;
  const xFor = (d: Date): number => (rangeStart ? dayDiff(rangeStart, d) * pxPerDay : 0);

  // Rows (section headers + task rows) with bar geometry; y measured from below the axis.
  const rows: GanttRow[] = [];
  const barById = new Map<string, GanttBar>();
  let y = axisHeight;
  for (const [sectionIndex, section] of spec.sections.entries()) {
    const isCollapsed = opts.collapsed?.has(sectionIndex) ?? false;
    rows.push({
      kind: 'section',
      name: section.name,
      sectionIndex,
      collapsed: isCollapsed,
      taskCount: section.tasks.length,
      y,
      height: sectionH,
    });
    y += sectionH;
    // A collapsed section shows only its header — its task rows (and bars) are omitted, so the
    // chart shrinks vertically. The date range above still counts every task, so the axis is stable.
    if (isCollapsed) continue;
    for (const task of section.tasks) {
      const r = resolved.get(task.id);
      const rowH = clampRowHeight(opts.rowHeights?.[task.id] ?? rowHeight);
      const barH = barHeightFor(rowH);
      const barPad = (rowH - barH) / 2;
      let bar: GanttBar | undefined;
      if (r) {
        const bx = xFor(r.start);
        const bw = r.milestone ? 0 : Math.max(3, dayDiff(r.start, r.end) * pxPerDay);
        bar = {
          id: task.id,
          title: task.title,
          owner: task.owner,
          status: task.status,
          milestone: task.milestone,
          percent: percentFor(task.status, task.milestone),
          x: bx,
          width: bw,
          y: y + barPad,
          height: barH,
          startDate: fmtDate(r.start),
          endDate: fmtDate(r.end),
        };
        barById.set(task.id, bar);
      }
      rows.push({
        kind: 'task',
        id: task.id,
        title: task.title,
        owner: task.owner,
        status: task.status,
        milestone: task.milestone,
        y,
        height: rowH,
        bar,
      });
      y += rowH;
    }
  }
  const bodyHeight = y - axisHeight;
  const totalHeight = y;
  // Stretch the drawn canvas down to fill the viewport so the grid doesn't stop mid-screen.
  const canvasHeight = Math.max(totalHeight, opts.minHeight ?? 0);

  // Two-tier axis + weekend shading + today.
  const upper: AxisBand[] = [];
  const ticks: AxisTick[] = [];
  const weekends: AxisBand[] = [];
  let todayX: number | null = null;
  if (rangeStart && rangeEnd) {
    // Upper band: months (day/week) or years (month zoom).
    if (zoom === 'month') {
      for (let y0 = rangeStart.getFullYear(); y0 <= rangeEnd.getFullYear(); y0 += 1) {
        const bx = Math.max(0, xFor(new Date(y0, 0, 1)));
        const bw = Math.min(timelineWidth, xFor(new Date(y0 + 1, 0, 1))) - bx;
        if (bw > 0) upper.push({ label: String(y0), x: bx, width: bw });
      }
      for (let d = monthStart(rangeStart); d <= rangeEnd; d = nextMonth(d)) {
        ticks.push({ x: xFor(d), label: MONTHS[d.getMonth()], strong: d.getMonth() === 0 });
      }
    } else {
      for (let d = monthStart(rangeStart); d <= rangeEnd; d = nextMonth(d)) {
        const bx = Math.max(0, xFor(d));
        const bw = Math.min(timelineWidth, xFor(nextMonth(d))) - bx;
        if (bw > 0) upper.push({ label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, x: bx, width: bw });
      }
      if (zoom === 'day') {
        for (let d = new Date(rangeStart); d <= rangeEnd; d = addDays(d, 1)) {
          ticks.push({ x: xFor(d), label: String(d.getDate()), strong: d.getDay() === 1 });
        }
      } else {
        for (let d = startOfWeek(rangeStart); d <= rangeEnd; d = addDays(d, 7)) {
          ticks.push({ x: xFor(d), label: `${d.getMonth() + 1}/${d.getDate()}`, strong: false });
        }
      }
    }
    if (zoom !== 'month') {
      for (let d = new Date(rangeStart); d <= rangeEnd; d = addDays(d, 1)) {
        const wd = d.getDay();
        if (wd === 0 || wd === 6) weekends.push({ label: '', x: xFor(d), width: pxPerDay });
      }
    }
    const today = opts.today;
    if (today && today >= rangeStart && today <= rangeEnd) todayX = xFor(today);
  }

  // Finish-to-start dependency elbows (predecessor right → dependent left).
  const deps: DepLink[] = [];
  for (const row of rows) {
    if (row.kind !== 'task' || !row.bar) continue;
    const task = resolved.get(row.id!)?.task;
    if (!task) continue;
    for (const dep of task.after) {
      const pb = barById.get(dep);
      if (pb) {
        deps.push({
          x1: pb.milestone ? pb.x : pb.x + pb.width,
          y1: pb.y + pb.height / 2,
          x2: row.bar.x,
          y2: row.bar.y + row.bar.height / 2,
        });
      }
    }
  }

  return {
    zoom,
    pxPerDay,
    labelWidth,
    timelineWidth,
    contentWidth,
    axisHeight,
    bodyHeight,
    totalHeight,
    canvasHeight,
    rangeStart,
    rows,
    upper,
    ticks,
    weekends,
    todayX,
    deps,
    hasBars: barById.size > 0,
  };
}
